import React, { useEffect, useState } from "react";
import FetchHelper from "../../../utils/FetchHelper";
import Notify from "../../../utils/Notify";

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function defaultHours() {
  return DAYS.map((day) => ({
    day,
    closed: true,
    opening_time: "",
    closing_time: "",
  }));
}

function normalizeHours(value) {
  const rows = Array.isArray(value) ? value : [];

  return defaultHours().map((fallback, weekday) => {
    const row = rows.find(
      (item) =>
        item.weekday != null &&
        Number(item.weekday) === weekday
    );

    return {
      day: fallback.day,
      closed: !row,
      opening_time: row?.opens_at?.slice(0, 5) || "",
      closing_time: row?.closes_at?.slice(0, 5) || "",
    };
  });
}
export default function VenueDepartments({
  venueId,
  categoryId,
  categoryTitle,
}) {
  if (
    venueId == null ||
    venueId === "" ||
    categoryId == null ||
    categoryId === ""
  ) {
    return (
      <div className="alert alert-warning">
        Select a venue and category to manage opening hours.
      </div>
    );
  }

  if (!window.Api?.Venues) {
    return (
      <div className="alert alert-danger">
        The venues API URL is missing.
      </div>
    );
  }

  return (
    <CategoryDepartmentSettings
      key={`${venueId}-${categoryId}`}
      venueId={venueId}
      categoryId={categoryId}
      categoryTitle={categoryTitle}
    />
  );
}

function CategoryDepartmentSettings({
  venueId,
  categoryId,
  categoryTitle,
}) {
  const [departmentId, setDepartmentId] = useState(null);
  const [mode, setMode] = useState("venue");
  const [hours, setHours] = useState(defaultHours);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [revision, setRevision] = useState(0);

  const collectionUrl =
    `${window.Api.Venues.replace(/\/+$/, "")}/` +
    `${encodeURIComponent(venueId)}/departments`;

  useEffect(() => {
    let active = true;

    async function loadSettings() {
      setLoading(true);
      setError("");
      setMessage("");

      try {
        const departments = [];
        const visited = new Set();
        let pageUrl = collectionUrl;

        while (pageUrl) {
          const currentUrl = new URL(pageUrl, window.location.origin);

          if (visited.has(currentUrl.href)) {
            throw new Error("Invalid department pagination.");
          }

          visited.add(currentUrl.href);

          const result = await FetchHelper.get(pageUrl);

          if (!active) return;

          const rows = Array.isArray(result)
            ? result
            : result?.results;

          if (!Array.isArray(rows)) {
            throw new Error("Unexpected departments response.");
          }

          departments.push(...rows);

          if (!Array.isArray(result) && result.next) {
            const nextUrl = new URL(result.next, currentUrl);

            if (nextUrl.origin !== currentUrl.origin) {
              throw new Error("Unexpected pagination origin.");
            }

            pageUrl = nextUrl.href;
          } else {
            pageUrl = null;
          }
        }

        // Match by category relationship, not by title or department ID.
        const matches = departments.filter((department) => {
          const linkedCategoryId =
            department.category?.id ?? department.category;

          return (
            linkedCategoryId != null &&
            String(linkedCategoryId) === String(categoryId)
          );
        });

        if (matches.length > 1) {
          throw new Error(
            "Multiple department records are linked to this category."
          );
        }

        // Avoid creating duplicates if the API uses a different
        // category relationship field.
        const hasUnknownRelationship = departments.some(
          (department) =>
            (department.category?.id ?? department.category) == null
        );

        if (matches.length === 0 && hasUnknownRelationship) {
          throw new Error(
            "The department response does not expose its category " +
              "relationship. Check the API category field before saving."
          );
        }

        const department = matches[0];

        if (department && department.id == null) {
          throw new Error("The department response is missing its ID.");
        }

        if (!active) return;

        setDepartmentId(department?.id ?? null);

        setMode(
          department?.temporarily_closed === true
            ? "closed"
            : department?.use_venue_hours === false
            ? "custom"
            : "venue"
        );

        setHours(normalizeHours(department?.weekly_hours));
      } catch (err) {
        if (active) {
          setError(err?.message || "Unable to load category settings.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      active = false;
    };
  }, [collectionUrl, categoryId, revision]);

  function updateDay(day, field, value) {
    setMessage("");

    setHours((current) =>
      current.map((row) =>
        row.day === day ? { ...row, [field]: value } : row
      )
    );
  }

  

  async function saveSettings(event) {
  event.preventDefault();

  if (saving || loading || error) return;

  const customDays = hours.filter((row) => !row.closed);

  if (mode === "custom") {
    if (customDays.length === 0) {
      Notify.error(
        'Add opening hours for at least one day, or select "Closed".'
      );
      return;
    }

    const invalidDay = customDays.find(
      (row) =>
        !row.opening_time ||
        !row.closing_time ||
        row.opening_time === row.closing_time
    );

    if (invalidDay) {
      Notify.error(
        `Enter different opening and closing times for ${invalidDay.day}.`
      );
      return;
    }
  }

  const creating = departmentId === null;

  const payload = {
    temporarily_closed: mode === "closed",
  };

  if (mode === "venue") {
    payload.use_venue_hours = true;
  }

  if (mode === "custom") {
    payload.use_venue_hours = false;

    // Clear the department-wide time overrides when saving
    // an explicit weekly schedule.
    payload.opens_at = null;
    payload.closes_at = null;

    payload.weekly_hours = customDays.map((row) => ({
      weekday: DAYS.indexOf(row.day),
      opens_at: `${row.opening_time}:00`,
      closes_at: `${row.closing_time}:00`,
    }));
  }

  if (creating) {
    payload.category = categoryId;

    // Venue comes from /venues/<venueId>/departments.
    // The category supplies the name; no title field is needed.
    if (mode === "closed") {
      payload.use_venue_hours = true;
    }
  }

  setSaving(true);
  setMessage("");

  try {
    if (creating) {
      const created = await FetchHelper.post(
        collectionUrl,
        payload
      );

      if (created?.id != null) {
        setDepartmentId(created.id);
      } else {
        setLoading(true);
        setRevision((value) => value + 1);
      }
    } else {
      await FetchHelper.patch(
        `${collectionUrl}/${encodeURIComponent(departmentId)}`,
        payload
      );
    }

    setMessage("Category opening settings saved.");
  } catch (err) {
    Notify.error(
      err?.message || "Unable to save category settings."
    );

    if (creating) {
      setLoading(true);
      setRevision((value) => value + 1);
    }
  } finally {
    setSaving(false);
  }
}




  return (
    <section className="card card-custom gutter-b mt-4">
      <div className="card-header">
        <div className="card-title">
          <h3 className="card-label">
            {categoryTitle || "Selected category"} — Opening hours
          </h3>
        </div>
      </div>

      <div className="card-body">
        {loading ? (
          <p role="status">Loading category settings...</p>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            <p>{error}</p>

            <button
              type="button"
              className="btn btn-light"
              onClick={() => setRevision((value) => value + 1)}
            >
              Retry
            </button>
          </div>
        ) : (
          <form onSubmit={saveSettings}>
            <fieldset disabled={saving}>
              <div className="form-group">
                <label htmlFor={`category-mode-${categoryId}`}>
                  Availability
                </label>

                <select
                  id={`category-mode-${categoryId}`}
                  className="form-control"
                  value={mode}
                  onChange={(event) => {
                    setMode(event.target.value);
                    setMessage("");
                  }}
                >
                  <option value="closed">Closed</option>
                  <option value="venue">
                    Use venue opening and closing hours
                  </option>
                  <option value="custom">
                    Set custom opening and closing hours
                  </option>
                </select>
              </div>

              {mode === "closed" && (
                <div className="alert alert-warning">
                  This category will remain closed until you change
                  this setting and save.
                </div>
              )}

              {mode === "venue" && (
                <div className="alert alert-info">
                  This category follows the venue’s opening and
                  closing hours for each day. When the venue is
                  closed, this category is closed.
                </div>
              )}

              {mode === "custom" && (
                <>
                  <div className="alert alert-info">
                    This category can open only while the venue is
                    open and within the custom hours below.
                    If the venue is closed, this category is closed.
                  </div>

                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          <th>Closed</th>
                          <th>Opening time</th>
                          <th>Closing time</th>
                        </tr>
                      </thead>

                      <tbody>
                        {hours.map((row) => (
                          <tr key={row.day}>
                            <th
                              scope="row"
                              className="text-capitalize"
                            >
                              {row.day}
                            </th>

                            <td>
                              <input
                                type="checkbox"
                                checked={row.closed}
                                aria-label={`Closed on ${row.day}`}
                                onChange={(event) =>
                                  updateDay(
                                    row.day,
                                    "closed",
                                    event.target.checked
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="time"
                                className="form-control"
                                value={row.opening_time}
                                disabled={row.closed}
                                required={!row.closed}
                                aria-label={`${row.day} opening time`}
                                onChange={(event) =>
                                  updateDay(
                                    row.day,
                                    "opening_time",
                                    event.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="time"
                                className="form-control"
                                value={row.closing_time}
                                disabled={row.closed}
                                required={!row.closed}
                                aria-label={`${row.day} closing time`}
                                onChange={(event) =>
                                  updateDay(
                                    row.day,
                                    "closing_time",
                                    event.target.value
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-muted">
                    Use the venue’s local timezone. A closing time
                    earlier than its opening time means the next day;
                    the backend must support this convention.
                  </p>
                </>
              )}

              {message && (
                <div className="alert alert-success" role="status">
                  {message}
                </div>
              )}

              <button type="submit" className="btn btn-primary">
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </fieldset>
          </form>
        )}
      </div>
    </section>
  );
}